// @ts-nocheck
/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState, useRef } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/message.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/divider.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/rating.css'

import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { CSSTransition } from 'react-transition-group'
import NumberFormat from 'react-number-format'
import { Link } from '@ta-interaktiv/contentbricks'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import Scrollchor from 'react-scrollchor'
import { gsap } from 'gsap'
import { TweenMax, ScrollToPlugin } from 'gsap/all'
import { CustomEase } from 'components/gsap/CustomEase'
import { questionList } from './questionList'
import { Question } from './question'
import DWChart from 'react-datawrapper-chart'
import { getEquivalence } from 'lib/equivalence'

import epflImg from './img/logo-epfl-1024x576.png'
import unilImg from './img/lo_unil06_bleu.png'
import diaperImg from './img/diaper.jpg'
import clothesImg from './img/clothes.jpg'
import milkImg from './img/milk.jpg'
import toysImg from './img/toys.jpg'
import foodImg from './img/food.jpg'
import frcImg from './img/sophie-michaud-gigon.jpg'

gsap.registerPlugin(ScrollToPlugin, CustomEase)

const ARTICLE_ID = '275356743320' // vs 140131201877
const publisherDomain = parseHostname().publicationDomain

function getAnswerById(id, answers) {
  const answer = answers.filter(a => a.id === id)
  if (answer.length === 1) {
    if (answer[0].answer !== null) {
      return answer[0].answer
    } else {
      console.warn('No answer found.')
      return false
    }
  }
  console.log('No answer yet for id', id)
  return false
}

function ArticleLink(props) {
  const link = publisherDomain === '24heures.ch' ? props.vqhLink : props.tdgLink
  return (
    <a href={link} target='_parent' className='item'>
      {props.text}
    </a>
  )
}

export function AppComponent() {
  const [result, setResult] = useState(null)
  const [questions, setQuestions] = useState([])
  const [topics, setTopics] = useState([])
  const schrollchorRef = useRef(null)
  const [currentQuestion, setCurrentQuestion] = useState(0)

  useEffect(() => {
    if (questions.length > 1 && currentQuestion === questions.length) {
      const totalEmissions = topics.reduce(
        (currentSum, topic) => currentSum + topic.result,
        0
      )
      setResult({
        sum: totalEmissions,
        equivalence: getEquivalence(totalEmissions)
      })
    }
  }, [currentQuestion])

  useEffect(() => {
    setQuestions(questionList)
  })

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Les gestes qui rendent votre bébé plus écologique</h1>
            <h2>
              La marge de manœuvre des parents est mince et le sujet est encore
              peu documenté. Utilisez notre calculateur pour identifier les
              gestes qui changent la donne.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Ghali Chraibi, Manon Michel et Guillaume Parchet, étudiants à
              l’EPFL
              <br />
              Avec la collaboration de Cécile Denayrouse et Paul Ronga
            </div>
            <div className='ta updated'>Mis à jour le 4 juillet 2021</div>
          </div>
          <div className='ui vertical segment'>
            <div
              className='ui text container'
              style={{
                'font-family': 'TamediaChart,Helvetica,Arial,sans-serif',
                'border-top': '3px solid #ccc',
                'padding-top': '10px'
              }}
            >
              <div className='ta text'>
                <p>
                  Cet article et le calculateur de pollution qui l’accompagne
                  ont été élaborés par trois étudiants de l’EPFL dans le cadre
                  du cours de Master UNIL-EPFL{' '}
                  <Link
                    allowWordWrap
                    href='https://www.epfl.ch/schools/cdh/fr/enseignement/cours-unil-epfl-2020-2021/les-donnees-en-contexte-critical-data-studies/'
                  >
                    «Les données en contexte»
                  </Link>
                  , en partenariat avec Tamedia. Deux journalistes les ont
                  encadrés dans leur travail.
                </p>
              </div>
              <div
                className='ui text container center aligned'
                style={{ 'border-bottom': '3px solid #ccc' }}
              >
                <div className='ui small images'>
                  <img className='ui image logo' src={unilImg} />
                  <img className='ui image logo' src={epflImg} />
                </div>
              </div>
            </div>
          </div>
          <Scrollchor ref={schrollchorRef} to='#questions' disableHistory />
          <div className='ta text'>
            <p>
              Couches, biberons, jouets… Tout le monde s’accorde à dire qu’avoir
              un bébé fait saigner le portefeuille. Mais ils ont un autre
              impact, plus difficilement quantifiable: le coût écologique. À
              elles seules, les 5500 couches-culottes utilisées en moyenne
              jusqu’aux débuts de la propreté d’un bébé, aux alentours de 2 ans
              et demi, nécessitent plus de 1500 litres de pétrole,{' '}
              <Link
                allowWordWrap
                href='https://zerowasteeurope.eu/wp-content/uploads/2019/11/bffp_report_mestrualitemsnappies_fr.pdf'
              >
                selon l’ONG Zero Waste Europe
              </Link>
              . Sans parler de leur transport et de leur élimination.
            </p>
            <p>
              Pour illustrer l’impact environnemental des bébés, mais aussi
              trouver des solutions pour réduire cet impact au quotidien, nous
              avons conçu un calculateur permettant d’estimer la pollution
              générée par différents types de couches, de nourriture, de
              vêtements et de jouets jusqu’au troisième anniversaire d’un
              enfant.
            </p>

            <p className='ui message'>
              <b>Note:</b> le calculateur proposé ci-dessous est approximatif,
              il ne tient pas compte de tous les paramètres qui influent sur le
              bilan écologique d’un bébé. Son but est de permettre une
              comparaison entre différentes alternatives du point de vue des
              émissions de gaz à effet de serre, mais il ne tient pas compte des
              autres formes de pollution.
              <br /> Les réponses ne sont pas enregistrées.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment questions-container' id='questions'>
        <div
          className='progress'
          style={{
            width: `${
              result === null
                ? ((1 + currentQuestion) / (questions.length + 2)) * 100
                : 100
            }%`
          }}
        />
        <div className='questions-content'>
          <div className='qwrap'>
            {questions.map(q => (
              <CSSTransition
                key={`question-${q.id}`}
                unmountOnExit
                in={currentQuestion === q.id && result === null}
                timeout={300}
                classNames='question'
              >
                <Question
                  handleClick={answer => {
                    // Register the answer
                    q.answer = answer

                    // Jump to a question
                    setTimeout(() => {
                      // Flow logic
                      if (typeof q.nextQuestionId !== 'undefined') {
                        console.log('Jumping to', q.nextQuestionId)
                        setCurrentQuestion(q.nextQuestionId)
                      } else if (typeof q.nextQuestion !== 'undefined') {
                        if (
                          q.nextQuestion.answerId !== null &&
                          q.nextQuestion.nextQuestionId !== null
                        ) {
                          const answer =
                            currentQuestion === q.nextQuestion.answerId
                              ? q.answer
                              : questions[q.nextQuestion.answerId].answer

                          if (
                            Object.prototype.hasOwnProperty.call(
                              q.nextQuestion.nextQuestionId,
                              answer
                            )
                          ) {
                            setCurrentQuestion(
                              q.nextQuestion.nextQuestionId[answer]
                            )
                          } else {
                            console.warn('Missing key for flow logic:', answer)
                          }
                        } else {
                          console.warn('Missing flow logic:', q.nextQuestion)
                        }
                      } else {
                        setCurrentQuestion(currentQuestion + 1)
                      }

                      // update the questions with this new answer
                      setQuestions([...questions])

                      if (q.result === true) {
                        // If true, we compute a result for this topic

                        // DIAPERS
                        if (q.topic === 'diapers') {
                          let topicTitle = ''
                          let emissions = 0

                          // Only use questions which got an answer
                          const diaperChoices = questions.filter(
                            question =>
                              question.topic === 'diapers' &&
                              question.answer !== null
                          )

                          if (diaperChoices[0].answer === 'jetable') {
                            emissions = 550
                            emissions *= diaperChoices[1].answer
                            topicTitle = 'Couches jetables'
                          } else {
                            topicTitle = 'Couches lavables'

                            if (diaperChoices[1].answer === 'externe') {
                              emissions = 650
                            } else {
                              emissions = 568.9
                              const contributingChoices = diaperChoices.filter(
                                q => q.id > 2
                              )
                              contributingChoices.forEach(item => {
                                emissions += item.answer
                              })
                            }
                          }

                          const equivalence = getEquivalence(emissions)

                          const newTopic = {
                            id: q.topic,
                            title: topicTitle,
                            result: Math.round(emissions),
                            equivalence: equivalence,
                            improvable: emissions > 500,
                            firstQuestion: diaperChoices[0].id
                          }
                          // Filter out duplicates
                          setTopics(topics => [
                            ...topics.filter(topic => topic.id !== newTopic.id),
                            newTopic
                          ])

                          // MILK
                        } else if (q.topic === 'milk') {
                          let emissions = 0
                          const milkChoices = questions.filter(
                            question =>
                              question.topic === 'milk' &&
                              question.answer !== null
                          )
                          let topicTitle = 'Lait'
                          if (milkChoices[0].answer === 'maternel') {
                            topicTitle += ' maternel'
                          } else if (milkChoices[0].answer === 'poudre') {
                            topicTitle += ' en poudre'
                          } else if (milkChoices[0] === 'mixte') {
                            topicTitle += ' (mixte)'
                          }
                          if (
                            ['maternel', 'mixte'].includes(
                              milkChoices[0].answer
                            )
                          ) {
                            const ateMore = getAnswerById(10, milkChoices)
                            if (ateMore === true) {
                              // ( I(Varié) * 71 + I(Végétarien) * 28.5 )
                              let months = getAnswerById(9, milkChoices)
                              const isVegetarian = getAnswerById(
                                11,
                                milkChoices
                              )
                              // We add half of the mix months
                              const mixMonths = getAnswerById(14, milkChoices)
                              months += mixMonths / 2
                              if (isVegetarian === true) {
                                emissions = 28.5 * months
                                topicTitle += ' (régime végétarien)'
                              } else {
                                emissions = 71 * months
                              }
                            }
                          }
                          if (
                            ['poudre', 'mixte'].includes(milkChoices[0].answer)
                          ) {
                            let months = getAnswerById(12, milkChoices)
                            const milkAmount = getAnswerById(13, milkChoices)
                            // We add half of the mix months
                            const mixMonths = getAnswerById(14, milkChoices)
                            months += mixMonths / 2
                            emissions += months * milkAmount * 10.5
                          }
                          const equivalence = getEquivalence(emissions)

                          const newTopic = {
                            id: q.topic,
                            title: topicTitle,
                            result: Math.round(emissions),
                            equivalence: equivalence,
                            improvable: emissions > 480,
                            firstQuestion: milkChoices[0].id
                          }
                          // Filter out duplicates
                          setTopics(topics => [
                            ...topics.filter(topic => topic.id !== newTopic.id),
                            newTopic
                          ])

                          // FOOD
                        } else if (q.topic === 'food') {
                          let emissions = 166.25
                          let topicTitle = 'Petits pots'
                          const foodChoices = questions.filter(
                            question =>
                              question.topic === 'food' &&
                              question.answer !== null
                          )
                          const foodType = getAnswerById(15, foodChoices)
                          if (foodType === 'industriel') {
                            if (foodChoices.length > 1) {
                              const packaging = getAnswerById(16, foodChoices)
                              if (packaging === 'verre') {
                                emissions = 302
                                topicTitle += ' en verre'
                              } else {
                                emissions = 266
                                topicTitle += ' en plastique'
                              }
                            }
                          }

                          const equivalence = getEquivalence(emissions)

                          const newTopic = {
                            id: q.topic,
                            title: topicTitle,
                            result: Math.round(emissions),
                            equivalence: equivalence,
                            improvable: false,
                            firstQuestion: foodChoices[0].id
                          }
                          // Filter out duplicates
                          setTopics(topics => [
                            ...topics.filter(topic => topic.id !== newTopic.id),
                            newTopic
                          ])

                          // TEDDY
                        } else if (q.topic === 'teddy') {
                          const topicTitle = 'Peluches'
                          const teddyChoices = questions.filter(
                            question =>
                              question.topic === 'teddy' &&
                              question.answer !== null
                          )
                          // nombre_peluches * avg_peluche_weigth * (pollu_polyester_per_kg + pollu_transport_China_CH)
                          const teddyCount = getAnswerById(17, teddyChoices)
                          let emissions = teddyCount * 0.2 * (5 + 0.1528)

                          // #peluches * #piles_par_peluche * proportion_piles * pollu_poduction_pile
                          const electricTeddyCount = getAnswerById(
                            18,
                            teddyChoices
                          )
                          emissions += electricTeddyCount * 2 * 0.1

                          const equivalence = getEquivalence(emissions)

                          const newTopic = {
                            id: q.topic,
                            title: topicTitle,
                            result: Math.round(emissions),
                            equivalence: equivalence,
                            improvable: emissions > 10,
                            firstQuestion: teddyChoices[0].id
                          }
                          // Filter out duplicates
                          setTopics(topics => [
                            ...topics.filter(topic => topic.id !== newTopic.id),
                            newTopic
                          ])
                          // TOYS
                        } else if (q.topic === 'toys') {
                          const topicTitle = 'Jouets'
                          const toysChoices = questions.filter(
                            question =>
                              question.topic === 'toys' &&
                              question.answer !== null
                          )
                          // TODO
                          const litreToM3 = 0.005
                          const nonEmptySpace = 0.2
                          const plasticVolumicMass = 1050
                          const woodVolumicMass = 800

                          const plasticToysVolume = getAnswerById(
                            19,
                            toysChoices
                          )

                          const woodenToysVolume = getAnswerById(
                            20,
                            toysChoices
                          )

                          const reuseCount = getAnswerById(21, toysChoices)

                          // (volume_en_L * convertion_L_to_m3 * masse_volumique_materiau * proportion_espace_occupée * (pollution_mat_per_kg + Pollution_transport) ) / #children_user
                          const plasticToysEmissions =
                            plasticToysVolume *
                            litreToM3 *
                            nonEmptySpace *
                            plasticVolumicMass *
                            (5 + 0.1528)
                          const woodenToysEmissions =
                            woodenToysVolume *
                            litreToM3 *
                            nonEmptySpace *
                            woodVolumicMass *
                            (1 + 0.0072)

                          let emissions =
                            plasticToysEmissions + woodenToysEmissions
                          if (reuseCount) {
                            emissions /= reuseCount
                          }

                          const equivalence = getEquivalence(emissions)

                          const newTopic = {
                            id: q.topic,
                            title: topicTitle,
                            result: Math.round(emissions),
                            equivalence: equivalence,
                            improvable: emissions > 500,
                            firstQuestion: toysChoices[0].id
                          }
                          // Filter out duplicates
                          setTopics(topics => [
                            ...topics.filter(topic => topic.id !== newTopic.id),
                            newTopic
                          ])

                          // CLOTHES
                        } else if (q.topic === 'clothes') {
                          let emissions = 546 // Baseline for 3 years
                          const topicTitle = 'Vêtements'
                          const clothesChoices = questions.filter(
                            question =>
                              question.topic === 'clothes' &&
                              question.answer !== null
                          )

                          const differenceFromBaseline = clothesChoices.reduce(
                            (currentSum, choice) =>
                              currentSum + parseFloat(choice.answer),
                            0
                          )
                          emissions += differenceFromBaseline

                          const equivalence = getEquivalence(emissions)

                          const newTopic = {
                            id: q.topic,
                            title: topicTitle,
                            result: Math.round(emissions),
                            equivalence: equivalence,
                            improvable: emissions > 1200,
                            firstQuestion: clothesChoices[0].id
                          }
                          // Filter out duplicates
                          setTopics(topics => [
                            ...topics.filter(topic => topic.id !== newTopic.id),
                            newTopic
                          ])
                        }
                      }
                      // schrollchorRef.current.simulateClick()
                    }, 500)
                  }}
                  data={q}
                />
              </CSSTransition>
            ))}
          </div>
          {result !== null && (
            <div className='ui vertical segment very fitted'>
              <div className='ta text chap-title'>Résultat</div>
            </div>
          )}
          {topics.length > 0 ? (
            <>
              <div className='ui vertical segment very fitted'>
                <div className='ui text container'>
                  <table className='ui compact grey table'>
                    <tbody>
                      {topics.map(item => {
                        return (
                          <tr
                            key={item.id}
                            className={item.improvable ? 'improvable' : ''}
                          >
                            <td className='first'>{item.title}</td>
                            <td>
                              {item.result} kg eqCO<sub>2</sub>*
                              {item.equivalence}
                            </td>
                            <td>
                              <i
                                className='edit icon'
                                onClick={() => {
                                  setCurrentQuestion(item.firstQuestion)
                                  setResult(null)
                                  schrollchorRef.current.simulateClick()
                                }}
                              />
                            </td>
                          </tr>
                        )
                      })}
                      {result ? (
                        <tr key='result' className='result'>
                          <td className='first'>Total</td>
                          <td>
                            {result.sum} kg eqCO<sub>2</sub>*
                            {result.equivalence}**
                          </td>
                          <td />
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                  <p>
                    * Kilos équivalents CO<sub>2</sub>. L’effet de serre est
                    causé par différents gaz, dont l’impact sur le réchauffement
                    climatique est plus ou moins fort. Le kg eqCO<sub>2</sub>{' '}
                    sert d’unité de base.
                  </p>
                  <p>
                    ** Dans une voiture consommant 7 litres au cent en moyenne.
                  </p>
                </div>
              </div>
              <div className='footer'>
                {/* <div className='ui text container center aligned'>
                <div
                  className='previous-question'
                  onClick={() => {
                    setCurrentQuestion(currentQuestion - 1)
                  }}
                >
                  <i className='ui icon chevron left' /> Question précédente
                </div>
              </div> */}
              </div>
            </>
          ) : null}
          {result !== null && (
            <div className='footer'>
              <div className='ui text container center aligned'>
                <div
                  className='previous-question'
                  onClick={() => {
                    setCurrentQuestion(0)
                    setResult(null)
                    schrollchorRef.current.simulateClick()
                  }}
                >
                  <i className='ui icon chevron left' /> Recommencer
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4 className='title'>Les pièges des couches lavables</h4>

            <p>
              La couche-culotte jetable est omniprésente, mais la quantité de
              déchets qu’elle génère – plus ou moins une tonne par bébé,{' '}
              <Link
                allowWordWrap
                href='https://zerowasteeurope.eu/wp-content/uploads/2019/11/bffp_report_mestrualitemsnappies_fr.pdf'
              >
                selon Zero Waste Europe
              </Link>{' '}
              – et les composants nocifs découverts dans certains modèles ont
              remis les couches lavables au goût du jour. Sans compter que ces
              dernières allègent considérablement le budget des parents,
              puisqu’elles permettent d’économiser environ 2000 francs en achat
              par rapport à des couches jetables.
            </p>
            <p>
              Ce marché a énormément évolué, comme l’a observé Vanessa Thiboud,
              enseignante dans une école genevoise et mère de trois enfants:
              «Lorsque j’ai eu mon premier enfant il y a quinze ans, on ne
              trouvait simplement pas de couches écologiques. Le débat était
              plutôt centré sur les produits toxiques.»
            </p>
            <p className='article link'>
              <b>Lire aussi:</b>{' '}
              <ArticleLink
                tdgLink='https://www.tdg.ch/21961664'
                vqhLink='https://www.24heures.ch/22911140'
                text='Les langes font polémique en France, la Suisse relativise'
              />
            </p>
            <p>
              Elle a pu se procurer des couches bios pour son deuxième enfant,
              âgé aujourd’hui de 12 ans, en les commandant sur internet: elles
              n’étaient pas disponibles en grande surface. «Mon choix visait à
              éviter les produits toxiques. J’ai également envisagé les couches
              lavables, mais leur utilisation me paraissait beaucoup trop
              fastidieuse.»
            </p>
            <p>
              Bien sûr, le choix de ces couches bios a un coût sonnant et
              trébuchant. Enseignant-chercheur à l’Université de Lausanne et à
              l’EPFL et papa de deux enfants, Selim Krichane estime les acheter
              «environ moitié plus cher que les couches premier prix».
            </p>
            <p>
              <img
                className='ui small circular left floated bordered image'
                src={diaperImg}
                alt='Couche-culotte jetable'
              />
              L’impact environnemental des couches bios est très variable,
              notamment en fonction de leur composition, de leur méthode de
              fabrication et de leur provenance. Mais une étude du gouvernement
              britannique remontant à 2008 estimait qu’elles permettent une
              réduction des émissions de gaz à effet de serre de l’ordre de 15%
              par rapport aux couches jetables classiques.
            </p>
            <p>
              Quant à l’empreinte carbone des couches lavables, elle dépend très
              fortement de la manière dont les parents les lavent, tout au long
              de la croissance du bébé. Machines à moitié vides, température
              réglée à 90 °C, séchage en machine: une seule de ces habitudes
              pourtant bien pratiques suffit à rendre les couches lavables plus
              polluantes que les jetables, selon la même étude. Et les progrès
              des appareils électroménagers en matière de consommation d’énergie
              n’ont pas encore changé la donne.
            </p>
            <p>
              Le fait de bien enlever les selles du bébé, assorti d’un lavage
              optimisé d’un point de vue énergétique, permet de faire chuter le
              poids écologique des couches lavables. Mais le coût de cette
              solution, en temps et en effort, la rend peu attractive pour la
              plupart des parents.
            </p>

            <DWChart title='Chart' src='//datawrapper.dwcdn.net/ArJ0g' />

            <h4 className='title'>Lait maternel = zéro émission?</h4>

            <p>
              Sein ou biberon, Vanessa Thiboud connaît les deux: «Après avoir
              allaité chacun de mes enfants durant les 1-2 premiers mois, je
              suis passée au lait en poudre. J’aurais préféré continuer
              d’allaiter, malgré les contraintes que cela implique, mais je
              manquais de lait en raison d’une réduction mammaire.»
            </p>
            <p>
              Nettement plus écologique que le lait en poudre, l’allaitement a
              tout de même un impact sur l’environnement. En effet, selon un
              rapport mandaté par l’OMS et l’ONU, une maman qui allaite consomme
              environ 500 kcal journalières supplémentaires durant les 6
              premiers mois de l’enfant. C’est donc le régime alimentaire de la
              maman, par exemple carné ou végétarien, qui détermine l’empreinte
              carbone du lait maternel.
            </p>
            <p className='article link'>
              <b>Lire aussi:</b>{' '}
              <ArticleLink
                tdgLink='https://www.tdg.ch/27188256'
                vqhLink='https://www.24heures.ch/25412994'
                text='Le CHUV biberonne son projet de banque de lait'
              />
            </p>
            <p>
              <img
                className='ui small circular right floated bordered image'
                src={milkImg}
                alt='Biberon de lait'
              />
              Celle des substituts de lait maternel est complexe à calculer.
              Nous pouvons toutefois en faire une approximation en prenant en
              compte les différentes étapes de leur fabrication. Ces
              préparations infantiles sont principalement composées d’huiles
              végétales et de lait brut. La production de ce dernier concentre
              près de 60% du coût carbone du produit final, mais fluctue
              grandement d’un pays à l’autre.
            </p>
            <p>
              De plus, d’autres facteurs de production ne sont pas à négliger,
              comme la transformation des matières brutes en poudre, l’emballage
              du produit et les divers transports. Enfin, au moment de la
              consommation, il est nécessaire de stériliser le lait avec de
              l’eau chaude dont la préparation est également énergivore.
            </p>

            <h4 className='title'>
              Les habits: petits mais pas pour autant écologiques
            </h4>

            <p>
              Production délocalisée, colorants polluants, consommation d’eau et
              de pétrole: l’industrie textile a un impact environnemental
              désastreux. Les vêtements de nos bébés ont beau être petits et
              légers, ils n’y échappent pas. Malgré l’inévitable fait que les
              bébés grandissent rapidement et se salissent en permanence,
              certains choix permettent de réduire l’impact environnemental
              engendré par leurs piles de bodies tachés de vomis.
            </p>
            <p>
              <img
                className='ui small circular left floated bordered image'
                src={clothesImg}
                alt='Séchage à l’air libre'
              />
              Le geste le plus important pour réduire cet impact est d’acheter
              ou récupérer des vêtements de seconde main, puis de les donner ou
              les revendre une fois que bébé ne rentre plus dedans. Tout comme
              pour les couches lavables, laver les vêtements avec des lessives à
              basse température (jusqu’à 40 °C), faire des machines pleines et
              sécher le linge à l’air libre participe à réduire son empreinte
              carbone.
            </p>
            <p>
              Dans le feu de l’action, les bonnes intentions sont difficiles à
              appliquer, souligne Selim Krichane: «Nous achetons trop de neuf à
              mon goût. Mais quand on s’occupe d’un nouveau-né ou d’un
              nourrisson, tout est compliqué!»
            </p>
            <p>
              «Un enfant, c’est énormément de choses à gérer, tu dors beaucoup
              moins, tu es parfois en mode survie. En tant que parent, même si
              on pense aux aspects environnementaux liés aux bébés, on manque
              donc d’énergie et de disponibilité pour les mettre en pratique,
              complète Jackson, 39 ans et papa de deux enfants de 3 et 5 ans.
              Mais nous avons eu la chance de recevoir beaucoup de vêtements que
              des amis avaient achetés pour leurs propres enfants.»
            </p>

            <h4 className='title'>Jouets: la jungle des labels</h4>

            <p>
              Qui n’a jamais cédé à l’envie de faire plaisir à un enfant en lui
              offrant une jolie peluche ou un nouveau jouet? Avec la venue du
              tout plastique et des jeux à bas prix, il est facile d’en amasser
              des caisses entières alors même que, selon{' '}
              <Link
                allowWordWrap
                href='https://www.sciencedirect.com/science/article/pii/S0163638317301613?via%3Dihub'
              >
                une étude de 2018
              </Link>
              , la surabondance de jouets nuit à la qualité de jeu. Outre les
              dépenses qu’ils représentent, leur transport et les difficultés de
              recyclage en font une problématique écologique digne d’intérêt.
            </p>
            <p className='article link'>
              <b>Lire aussi:</b>{' '}
              <ArticleLink
                tdgLink='https://www.tdg.ch/13211378'
                vqhLink='https://www.24heures.ch/31256248'
                text='La tablette numérique n’a rien d’une baby-sitter'
              />
            </p>
            <p>
              <img
                className='ui small circular right floated bordered image'
                src={toysImg}
                alt='Jouets pour bébés'
              />
              Jackson l’a constaté empiriquement: «Plus ils ont de jouets, moins
              ils jouent avec… Mais avec les anniversaires, la famille, les
              caprices quand on va dans les magasins, il est très difficile de
              respecter ses principes. Il arrive qu’on cède.»
            </p>
            <p>
              Dans ce domaine en particulier, il est troublant de constater
              l’opacité entretenue par les grands producteurs quant à la
              provenance, la composition ou la fin de vie de leurs produits. Les
              grandes marques que nous avons contactées ont refusé de répondre à
              nos questions concernant la provenance ou les méthodes de
              production de leurs produits.
            </p>
            <p>
              Selon{' '}
              <Link
                allowWordWrap
                href='https://www.fimif.fr/wp-content/uploads/2017/03/Enquete-FIMIF-Jeux-Jouets-Made-in-France-Novembre-2016.pdf'
              >
                une enquête française
              </Link>{' '}
              menée par la Fédération indépendante du Made in France, les
              produits de la petite enfance, comme les doudous ou les jeux
              d’éveil, ne disposent de pratiquement aucune indication d’origine.
              Certains fabricants vont même jusqu’à ajouter des indicateurs
              trompeurs laissant à penser que la production est locale, alors
              que ce n’est pas le cas.
            </p>
            <p>
              Il est donc difficile pour le consommateur d’effectuer des choix
              éclairés lors de ses achats. Privilégier les producteurs de jeux
              locaux reste la meilleure alternative pour réduire les émissions
              associées aux transports. On pourra également favoriser les jeux
              en bois brut – et non en bois aggloméré car ceux-ci contiennent du
              méthanal, une substance cancérigène selon le Centre international
              de recherche sur le cancer – ou certaines peluches en coton.
              Cependant, baser son choix sur ces matériaux uniquement n’est pas
              encore une garantie d’écoresponsabilité.
            </p>
            <p>
              Pour les plus concernés, il s’agira alors de se renseigner sur la
              provenance et les labels offerts. Idéalement, un jouet dit
              écologique devrait adhérer à la{' '}
              <Link
                allowWordWrap
                href='https://www.iso.org/fr/iso-14001-environmental-management.html'
              >
                norme ISO 14 000
              </Link>{' '}
              sur le respect environnemental. Pour les jeux en bois brut, les
              labels FSC ou PEFC peuvent également être de bons indicateurs pour
              privilégier des bois gérés de manière durable. Finalement, le
              marquage CE, la norme EN 71-3 ou les labels contre les produits
              nocifs tels que Oeko-Tex, SpielGut, l’ange bleu et{' '}
              <Link href='https://www.futura-sciences.com/planete/questions-reponses/eco-consommation-reconnaitre-produits-menagers-ecologiques-934/'>
                NF environnement
              </Link>{' '}
              peuvent vous guider dans vos choix.
            </p>
            <p>
              De manière plus globale, il est également possible de s’appuyer
              sur{' '}
              <Link allowWordWrap href='https://moralscore.org/sectors/jouets/'>
                le comparatif de Moralscore
              </Link>
              , qui attribue des notes aux marques de jouets sur la base de
              critères sociaux, environnementaux et éthiques.
            </p>

            <h4 className='title'>Le casse-tête du transport</h4>

            <p>
              <img
                className='ui small circular left floated bordered image'
                src={foodImg}
                alt='Jouets pour bébés'
              />
              On néglige trop souvent le transport nécessaire à nos achats
              lorsque l’on cherche à réduire notre empreinte carbone. Pourtant
              l’origine et même le poids des marchandises destinées aux bébés
              peuvent les rendre plus polluantes. Certes, un pot de purée en
              verre se recycle mieux et ne risque pas de contenir du bisphénol,
              une substance très controversée pour ses effets sur la santé. Mais
              en raison de son poids, il peut avoir un coût carbone plus élevé
              que son homologue plastique lors des transports.
            </p>
            {/* <DWChart
              className='desktop only grid'
              title='Chart'
              src='//datawrapper.dwcdn.net/tQDZM'
            /> */}
            <DWChart title='Chart' src='//datawrapper.dwcdn.net/FSmVf' />
            <p>
              Autre constat déroutant: en général, faire ses achats en ligne est
              nettement moins polluant que de se rendre en voiture dans un
              magasin, selon les conclusions d’une{' '}
              <Link
                allowWordWrap
                href='https://www.sciencedirect.com/science/article/abs/pii/S1361920919302639?casa_token=5F_3LW45LZkAAAAA:Cwd2rmVZYu2ro0J-Fh9JiRm8ZmK_K2R7Al9-6hYIuC3BVO1AfxzcBuMc9jY0LFPD-meu4SbvHA'
              >
                étude américaine parue en 2020
              </Link>
              . Pour schématiser, un camion de livraison bien rempli génère
              moins de pollution que des dizaines de personnes se déplaçant
              individuellement.
            </p>
            <p>
              En 2018, 86% des vêtements pour bébés et 80% des poussettes
              importées en Suisse provenaient d’Asie, selon l’Administration
              fédérale des douanes. Dans ces conditions, et alors que les
              fabricants entretiennent la confusion entre conception et
              fabrication locale, la marge de manœuvre des consommateurs est
              réduite.
            </p>

            <DWChart title='Chart' src='//datawrapper.dwcdn.net/ISQ9t' />

            <p>
              Enfin, comme le relève Sophie Michaud Gigon, secrétaire générale
              de la FRC, le choix écologique «n’est pas aisé pour tout le monde
              et peut prendre du temps ou de l’argent». Le fait maison, certes
              plus économique, alourdit les tâches ménagères. Quant aux produits
              estampillés «bio» des grandes surfaces, «ils sont clairement plus
              chers car la distribution prend des marges trop grandes, poursuit
              Sophie Michaud Gigon. Trouver le bon produit demande du temps,
              surtout si vous cherchez à cumuler tous les critères (écologie,
              santé, prix, etc.), ils peuvent même s’opposer et l’information
              laisse à désirer.» Une charge supplémentaire peu compatible avec
              une vie de parents surchargés et souvent minée par des nuits en
              dents de scie.
            </p>
            <p />
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h3 className='title'>
              «Il y a clairement un manque de volonté politique»
            </h3>

            <img
              className='ui small circular image'
              src={frcImg}
              alt='Sophie Michaud Gigon, secrétaire générale de la FRC'
            />
            <p>
              <b>Sophie Michaud Gigon</b>, secrétaire générale de la FRC
            </p>

            <p>
              <b>
                Pourquoi avons-nous si peu d’informations sur l’origine réelle
                des marchandises de puériculture?
              </b>
              <br />
              C’est un problème qui dépasse largement la puériculture. À la FRC,
              nous attendons avec impatience les avancées de l’UE autour de
              l’écolabel. Cependant, les critères peuvent se cumuler ou
              s’opposer. Pour les enfants, l’aspect santé est prioritaire. Or
              les fabricants n’ont pas les substances indésirables en ligne de
              mire. Nous l’avons notamment constaté en analysant des maquillages
              vendus au rayon jouets: sept des neuf produits analysés
              contenaient des substances hautement indésirables pour la santé.
              Le manque de traçabilité de ces articles est également
              problématique: dans le domaine des jouets par exemple, il est
              difficile de savoir où, comment et avec quoi ils ont été produits.
            </p>

            <p>
              <b>
                La Confédération manque-t-elle de volonté politique en la
                matière?
              </b>
              <br />
              En général, la position des autorités suisses est de laisser
              l’économie prendre des mesures sur une base volontaire, à
              l’exception quand même de tout ce qui concerne la sécurité. Dans
              le domaine des articles destinés aux enfants, il y a clairement un
              manque de volonté: on laisse les fabricants et l’industrie
              agroalimentaire faire comme bon leur semble en intervenant au
              minimum.
            </p>

            <p>
              <b>
                Comment garder le cap de l’écologie quand cela alourdit les
                tâches ménagères?
              </b>
              <br />
              Les premiers choix en matière de démarches écologiques sont
              souvent faits par les mères durant leur congé maternité. Et comme
              ce sont souvent les mères qui se préoccupent de ces aspects, voire
              gèrent beaucoup de tâches ménagères, cela pèse sur leur quotidien
              et leur charge mentale… Il y a donc encore beaucoup à faire pour
              parvenir à l’égalité dans ce domaine et la responsabilité
              vis-à-vis de son empreinte environnementale (et celle de ses
              enfants) doit être facilitée pour les jeunes parents, comme pour
              le reste de la population d’ailleurs.
            </p>
          </div>
        </div>
      </div>

      <div className='ui horizontal divider' />

      <div className='ui vertical grey-special segment' id='methode'>
        <div className='ui ta text container' style={{ position: 'relative' }}>
          <div className='innerWrapperMethodik'>
            <h4 className='title'>
              Pourquoi s’intéresser à l’impact écologique des bébés?
            </h4>

            <p>
              Il est surprenant de constater qu’en 2021 il reste tant de
              produits liés à la petite enfance dont nous ignorons tout de
              l’empreinte écologique. Ce manque d’informations nous a encouragés
              à entreprendre des recherches à travers lesquelles nous espérons
              informer et faire un premier pas pour plus de transparence de la
              part des entreprises.
            </p>

            <h4 className='title'>Comment nous avons procédé</h4>

            <p>
              Pour poser les bases de l’article, nous avons lu de nombreuses
              publications scientifiques et contacté des experts de l’industrie
              et du monde académique, afin d’obtenir des informations dans un
              domaine encore relativement peu documenté.
            </p>
            <p>
              Afin d’aider les lecteurs à identifier l’impact environnemental de
              leurs choix, nous avons conçu un calculateur d’empreinte
              écologique. La problématique étant très vaste, nous nous sommes
              focalisés sur l’alimentation, les vêtements, les couches et les
              jouets des bébés. Les résultats sont approximatifs en raison du
              manque de données disponibles sur les bilans carbones des produits
              de puériculture, mais aussi de tous les paramètres dont il est
              impossible de tenir compte.
            </p>

            <h4 className='title'>Ce que nous retenons</h4>

            <p>
              Même si certains acteurs font un réel effort de transparence, le
              coût carbone ne devient intéressant que lorsqu’il couvre tout le
              processus de fabrication d’un produit. Il faut donc que chaque
              maillon de la chaîne coopère (récolte des matières premières,
              transport, production et élimination). S’il est important que les
              jeunes parents soient sensibilisés à l’impact écologique des
              produits destinés à leur bébé, il est primordial que le secteur
              industriel et les politiques publiques fassent un effort de
              transparence.
            </p>

            <h4 className='title'>Pour en savoir plus</h4>
            <p className='bibliography'>
              <Link
                allowWordWrap
                href='https://dyper.com/images/UK-CO2-Study.pdf'
              >
                An updated lifecycle assessment study for disposable and
                reusable nappies
              </Link>{' '}
              (2008)
            </p>
            <p className='bibliography'>
              <Link
                allowWordWrap
                href='https://zerowasteeurope.eu/wp-content/uploads/2019/11/bffp_report_mestrualitemsnappies_fr.pdf'
              >
                Les coûts environnementaux et économiques des protections
                menstruelles, couches pour bébé et lingettes à usage unique
              </Link>{' '}
              (2019)
            </p>
            <p className='bibliography'>
              <Link
                allowWordWrap
                href='https://link.springer.com/content/pdf/10.1007%2Fs11367-008-0052-6.pdf'
              >
                Life cycle assessment of two baby food packaging alternatives:
                glass jars vs. plastic pots
              </Link>{' '}
              (2009)
            </p>
            <p className='bibliography'>
              <Link
                allowWordWrap
                href='https://www.agrarforschungschweiz.ch/en/2021/03/klir-a-model-for-calculating-greenhouse-gas-emissions-from-dairy-farms/'
              >
                KLIR: A Model for Calculating Greenhouse Gas Emissions from
                Dairy Farms
              </Link>{' '}
              (2021)
            </p>
            <p className='bibliography'>
              <Link
                allowWordWrap
                href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6472111/'
              >
                The carbon footprint of breastmilk substitutes in comparison
                with breastfeeding
              </Link>{' '}
              (2019)
            </p>
            <p className='bibliography'>
              <Link allowWordWrap href='https://www.bilans-ges.ademe.fr/'>
                Centre de ressources sur les bilans de gaz à effet de serre
              </Link>
            </p>
            <p className='bibliography'>
              <Link
                allowWordWrap
                href='https://www.ecoinvent.org/database/database.html'
              >
                EcoInvent
              </Link>{' '}
              (base de données sur le cycle de vie des produits)
            </p>
          </div>
        </div>
      </div>

      <Footer
        title='Comment notez-vous ce contenu?'
        questions={[
          'Est-il informatif?',
          'Le calculateur fonctionne-t-il correctement?',
          'Le calculateur est-il facile à utiliser?',
          'La mise en forme vous plaît-elle?',
          'Voudriez-vous voir davantage de ce type de contenu?'
        ]}
        mailTo='paul.ronga@tamedia.ch'
        hidePortfolioTeaser
        locale='fr'
        articleId={ARTICLE_ID}
        credits={[
          [
            'Textes et recherches',
            'Ghali Chraibi, Manon Michel, Guillaume Parchet (étudiants à l’EPFL)'
          ],
          [
            'Supervision',
            'Cécile Denayrouse, Charlotte Mazel-Cabasse, Paul Ronga'
          ],
          ['Développement', 'Paul Ronga, Mathieu Rudaz'],
          ['Images', 'Shutterstock']
        ]}
      />
    </div>
  )
}
